/**
 * Default-avatar registry — surfaces the commissioned
 * {@code apps/mobile/src/assets/avatar/default/player_avatar_NN_*.png}
 * set as a typed lookup. Twelve player faces, each with two states
 * (idle for normal display, lost_prize for wheel-payout commiseration
 * moments). Matches the asset brief's "default avatars" pack.
 *
 * <p>Stage 3 install-excision (2026-06-06) — the URLs used to come
 * from a Vite glob that bundled all 24 PNGs (~30MB) into the install.
 * The set is now path-only and resolved through
 * {@link resolveEntryUrl}, so production builds fetch from CloudFront
 * via the asset manifest. Adding a new avatar means dropping the PNG
 * AND bumping {@link AVATAR_COUNT} below — the toolgen / asset-sync
 * pipeline then auto-publishes the new file to S3.
 */
import { resolveEntryUrl } from '@goodtime/engine';

/** Number of commissioned default avatars on disk. Hand-maintained:
 *  bump when adding a new {@code player_avatar_NN_<state>.png} pair
 *  to {@code apps/mobile/src/assets/avatar/default/}. */
const AVATAR_COUNT = 12;

export type DefaultAvatarState = 'idle' | 'lost_prize';

export interface DefaultAvatar {
  /** Stable identifier — the {@code NN} portion of the filename, zero
   * padded ('01' .. '12'). Stored in localStorage and round-tripped
   * through {@link getDefaultAvatarUrl}. */
  id: string;
}

/** Path under {@code apps/mobile/src/assets/} for the given avatar
 *  id + state — matches the AssetManifestService's key format. */
function avatarPath(id: string, state: DefaultAvatarState): string {
  return `avatar/default/player_avatar_${id}_${state}.png`;
}

const REGISTRY: DefaultAvatar[] = Array.from(
  { length: AVATAR_COUNT },
  (_, i) => ({ id: String(i + 1).padStart(2, '0') }),
);

/** Every default avatar in display order ('01', '02', ..., '12'). */
export const DEFAULT_AVATARS: readonly DefaultAvatar[] = REGISTRY;

/** Resolve the URL for a given avatar id + state. Falls back to
 * {@code idle} when the requested state isn't reachable, then to the
 * first registered avatar when the id itself is unknown (defensive —
 * the localStorage key could carry a stale value after an asset
 * delete). Returns {@code null} when the manifest hasn't loaded AND
 * we're not in dev mode (the {@link resolveEntryUrl} dev-fallback
 * covers local iteration).
 */
export function getDefaultAvatarUrl(
  id: string | null | undefined,
  state: DefaultAvatarState = 'idle',
): string | null {
  if (REGISTRY.length === 0) return null;
  const avatar = REGISTRY.find((a) => a.id === id) ?? REGISTRY[0]!;
  // Try the requested state, fall back to idle if it doesn't resolve.
  const primary = resolveEntryUrl({ path: avatarPath(avatar.id, state) });
  if (primary) return primary;
  if (state !== 'idle') {
    const idle = resolveEntryUrl({ path: avatarPath(avatar.id, 'idle') });
    if (idle) return idle;
  }
  return null;
}

/** First avatar in the registry — used as the implicit default before
 * the player makes a selection. */
export const DEFAULT_AVATAR_ID = REGISTRY[0]?.id ?? null;
